import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { DashboardSection } from './DashboardSection';
import { Heading, Text } from '../../design-system/components/Typography';
import { Stack, Flex } from '../../design-system/components/Layout';
import { Badge } from '../../design-system/components/Decorative';
import { Avatar } from '../common/Avatar';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { InstitutionPersonView } from '../../types/dashboard';

const Panel = styled(DashboardSection)``;

const PeopleList = styled.ul`
  list-style: none;
  margin: ${spacing[5]} 0 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
`;

const PersonItem = styled.li`
  padding: ${spacing[4]} 0;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const PersonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: ${spacing[3]} ${spacing[5]};
  margin-top: ${spacing[3]};
`;

const FieldLabel = styled(Text)`
  display: block;
  margin-bottom: ${spacing[1]};
`;

const FieldValue = styled(Text)`
  font-weight: ${typography.fontWeight.medium};
`;

interface InstitutionPeoplePanelProps {
  people: InstitutionPersonView[];
}

export const InstitutionPeoplePanel: React.FC<InstitutionPeoplePanelProps> = ({ people }) => {
  const { t } = useTranslation();

  return (
    <Panel>
      <Heading variant="h3" as="h2" style={{ fontSize: typography.fontSize.section }}>
        {t('dashboard.institution.people.title')}
      </Heading>
      <Text size="sm" color="secondary">
        {t('dashboard.institution.peopleTabDesc')}
      </Text>

      {people.length === 0 ? (
        <Text size="sm" color="secondary" style={{ marginTop: spacing[5] }}>
          {t('dashboard.institution.noPeople')}
        </Text>
      ) : (
        <PeopleList>
          {people.map(person => (
            <PersonItem key={person.id}>
              <Flex direction="row" gap="md" align="start">
                <Avatar name={person.displayName} size="sm" />
                <Stack direction="column" spacing="sm" style={{ flex: 1, minWidth: 0 }}>
                  <Flex direction="row" gap="sm" align="center" wrap="wrap">
                    <Badge size="sm" color="accent" shape="pill">
                      {t(person.roleLabelKey)}
                    </Badge>
                  </Flex>
                  <FieldValue size="base" color="text">
                    {person.displayName}
                  </FieldValue>
                  <PersonGrid>
                    {person.position && (
                      <div>
                        <FieldLabel size="xs" color="secondary">
                          {t('register.icomRepresentativePosition')}
                        </FieldLabel>
                        <FieldValue size="sm" color="text">
                          {person.position}
                        </FieldValue>
                      </div>
                    )}
                    {person.email && (
                      <div>
                        <FieldLabel size="xs" color="secondary">
                          {t('common.email')}
                        </FieldLabel>
                        <FieldValue size="sm" color="text" style={{ wordBreak: 'break-word' }}>
                          {person.email}
                        </FieldValue>
                      </div>
                    )}
                    {person.phone && (
                      <div>
                        <FieldLabel size="xs" color="secondary">
                          {t('common.phone')}
                        </FieldLabel>
                        <FieldValue size="sm" color="text">
                          {person.phone}
                        </FieldValue>
                      </div>
                    )}
                    {person.committeeName && (
                      <div>
                        <FieldLabel size="xs" color="secondary">
                          {t('register.internationalCommittee')}
                        </FieldLabel>
                        <FieldValue size="sm" color="text">
                          {person.committeeName}
                        </FieldValue>
                      </div>
                    )}
                  </PersonGrid>
                </Stack>
              </Flex>
            </PersonItem>
          ))}
        </PeopleList>
      )}
    </Panel>
  );
};

export default InstitutionPeoplePanel;
